import React, { useEffect, useRef } from 'react';

export const LatticeBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener('resize', resize);
    resize();

    const particles: { x: number; y: number; vx: number; vy: number }[] = [];
    const particleCount = 100;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
      });
    }

    let time = 0;

    const draw = () => {
      time += 0.016; // Approx 60fps
      ctx.clearRect(0, 0, width, height);
      
      // Miyake Pulse Ripple (1.09s constant)
      // 1.09 seconds = 1090ms. At 60fps, that's ~65 frames per pulse.
      const pulseFrequency = (2 * Math.PI) / (1.09 * 60); 
      const pulseX = width / 2;
      const pulseY = height / 2;
      const pulseRadius = (Math.sin(time * 2) * 0.5 + 0.5) * Math.max(width, height);

      ctx.strokeStyle = 'rgba(0, 255, 204, 0.03)';
      ctx.lineWidth = 0.5;

      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        // Pulse Interaction
        const dx = p.x - pulseX;
        const dy = p.y - pulseY;
        const distToPulse = Math.sqrt(dx * dx + dy * dy);
        const pulseEffect = Math.max(0, 1 - Math.abs(distToPulse - pulseRadius) / 200);

        ctx.beginPath();
        ctx.arc(p.x, p.y, 1 + pulseEffect * 2, 0, Math.PI * 2);
        ctx.fillStyle = pulseEffect > 0.5 
          ? `rgba(0, 255, 204, ${0.3 + pulseEffect * 0.7})` 
          : 'rgba(0, 255, 204, 0.3)';
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx2 = p.x - p2.x;
          const dy2 = p.y - p2.y;
          const dist = Math.sqrt(dx2 * dx2 + dy2 * dy2);

          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = pulseEffect > 0.5 
              ? `rgba(0, 255, 204, ${0.1 + pulseEffect * 0.2})` 
              : 'rgba(0, 255, 204, 0.1)';
            ctx.stroke();
          }
        }
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 -z-10 opacity-50 pointer-events-none"
    />
  );
};
